import { useCallback, useEffect, useState } from "react";
import { MapPin, Bell, Smartphone, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pushStatus, enablePush, needsInstall, pushSupported } from "@/lib/push";

const GEO_KEY = "ms_geo_ok";

async function geoGranted() {
  const ok = localStorage.getItem(GEO_KEY) === "1";
  try {
    const p = await navigator.permissions?.query({ name: "geolocation" });
    // Safari iOS reports "prompt" even after the user allowed: trust a successful fix unless explicitly denied
    if (p) return p.state === "granted" || (ok && p.state !== "denied");
  } catch {
    // permissions API non disponibile
  }
  return ok;
}

const askGeo = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("Geolocalizzazione non disponibile"));
  navigator.geolocation.getCurrentPosition(() => { localStorage.setItem(GEO_KEY, "1"); res(); },
    () => rej(new Error("Consenti l'accesso alla posizione nelle impostazioni del telefono e riprova")), { enableHighAccuracy: true, timeout: 20000 });
});

function Step({ n, done, Icon, title, children }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 ${done ? "opacity-60" : ""}`} data-testid={`onboarding-step-${n}`}>
      <p className="mb-2 flex items-center gap-2 font-semibold">
        {done ? <CheckCircle2 className="h-5 w-5 text-[#61a229]" /> : <Icon className="h-5 w-5 text-[#61a229]" />} {n}. {title}
      </p>
      {!done && children}
    </div>
  );
}

export function TechOnboarding({ children }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = useCallback(async () => {
    const tr = (await api.get("/tech/tracking/status")).data;
    const geo = !tr.enabled || (tr.consented && (await geoGranted()));
    setSt({ tr, geo, push: !tr.push_required || (await pushStatus()) === "enabled", install: needsInstall(), unsupported: !pushSupported() });
  }, []);
  useEffect(() => { check().catch(() => setSt({ geo: true, push: true })); }, [check]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e.message || "Operazione non riuscita"); } finally { setBusy(false); check(); }
  };
  const activateGeo = () => run(async () => { await api.post("/tech/tracking/consent", { accept: true }); await askGeo(); });

  if (!st) return null;
  if (st.geo && st.push) return children;
  return (
    <div className="space-y-4 py-2" data-testid="tech-onboarding">
      <div>
        <h1 className="font-display text-2xl font-bold">Benvenuto in Madonna Store</h1>
        <p className="text-sm text-slate-500">Per usare l'app completa questi passaggi obbligatori.</p>
      </div>
      <Step n={1} done={st.geo} Icon={MapPin} title="Attiva la geolocalizzazione">
        <p className="mb-3 max-h-48 overflow-y-auto whitespace-pre-line rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="onboarding-consent-text">{st.tr?.consent_text}</p>
        <Button onClick={activateGeo} disabled={busy} className="w-full rounded-full bg-[#61a229] hover:bg-[#528a22]" data-testid="onboarding-geo-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accetto e attivo la posizione
        </Button>
      </Step>
      <Step n={2} done={st.push} Icon={Bell} title="Attiva le notifiche">
        {st.install ? (
          <p className="flex gap-2 text-sm text-slate-600" data-testid="onboarding-install"><Smartphone className="h-5 w-5 shrink-0" /> Su iPhone aggiungi prima l'app alla schermata Home: tocca Condividi e poi "Aggiungi alla schermata Home", poi apri l'app dall'icona.</p>
        ) : st.unsupported ? (
          <p className="text-sm text-red-600" data-testid="onboarding-unsupported">Questo browser non supporta le notifiche: usa Chrome su Android o l'app installata su iPhone.</p>
        ) : (
          <Button onClick={() => run(enablePush)} disabled={busy || !st.geo} className="w-full rounded-full" data-testid="onboarding-push-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Attiva notifiche
          </Button>
        )}
      </Step>
    </div>
  );
}
